/**
 * Utility functions for phone number handling in RxRooster application
 */

/**
 * Normalizes a phone number to E.164 format (e.g., +11234567890)
 * @param {string} phone - The phone number to normalize
 * @returns {string} - The normalized phone number
 */
export const normalizePhoneNumber = (phone) => {
  if (!phone) return '';
  
  // Remove all non-digit characters
  const digits = phone.replace(/\D/g, '');
  
  // Handle US numbers
  if (digits.length === 10) {
    return `+1${digits}`;
  }
  
  // Handle numbers that already have country code
  if (digits.length === 11 && digits.startsWith('1')) {
    return `+${digits}`;
  }
  
  // If already in E.164 format, return as is
  if (phone.startsWith('+')) {
    return phone;
  }
  
  // Default case - just add + if it's not there
  return `+${digits}`;
};

/**
 * Validates if a phone number is in a correct format
 * @param {string} phone - The phone number to validate
 * @returns {boolean} - Whether the phone number is valid
 */
export const isValidPhoneNumber = (phone) => {
  if (!phone) return false;
  
  // Basic validation - E.164 format requires +[country code][number]
  // For US, this is typically +1 followed by 10 digits
  const e164Regex = /^\+[1-9]\d{1,14}$/;
  
  return e164Regex.test(normalizePhoneNumber(phone));
};

/**
 * Formats a phone number for display (e.g., (123) 456-7890)
 * @param {string} phone - The phone number to format
 * @returns {string} - The formatted phone number for display
 */
export const formatPhoneForDisplay = (phone) => {
  if (!phone) return '';
  
  const normalized = normalizePhoneNumber(phone);
  
  // For US numbers, format as (XXX) XXX-XXXX
  if (normalized.startsWith('+1') && normalized.length === 12) {
    const digits = normalized.slice(2); // Remove +1
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  
  // For other numbers, just return the normalized version
  return normalized;
};
